// ===========================================
// A source or closing-reason picker with "+ Add" (enquiries plan L5, L6, §5.7)
// ===========================================
// Offers what a NEW enquiry may pick (active, selectable: never "Event" or "Unknown (migrated)").
// Any member of staff with enquiries.write may add an option; renaming and hiding are the owner
// tier's, in Settings → Enquiries. Adding a name that already exists selects the existing row;
// a hidden one is the server's 409, shown as it says.

import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import type { EnquiryListKind } from '../../../api/enquiry.api';
import { useAddEnquiryOption, useEnquiryOptions } from '../../../hooks/useEnquiries';
import { usePermissions } from '../../../hooks/usePermissions';

export interface EnquiryOptionSelectProps {
  kind: EnquiryListKind;
  label: string;
  value: string;
  onChange: (optionId: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

export default function EnquiryOptionSelect({
  kind,
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  disabled,
  size = 'small',
}: EnquiryOptionSelectProps) {
  const { can } = usePermissions();
  const mayAdd = can('enquiries', 'write');
  const { data: options, isLoading, isError } = useEnquiryOptions(kind, { forNewEnquiry: true });
  const addMutation = useAddEnquiryOption();
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      const { option } = await addMutation.mutateAsync({ kind, label });
      onChange(option.id);
      setNewLabel('');
      setAdding(false);
    } catch {
      // The hook reports the server's reason; stay in add mode with what was typed.
    }
  };

  const list = Array.isArray(options) ? options : [];
  const id = `enquiry-option-${kind.toLowerCase()}`;

  if (adding) {
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          autoFocus
          size={size}
          fullWidth
          label={`New ${label.toLowerCase()}`}
          value={newLabel}
          inputProps={{ maxLength: 80 }}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleAdd();
            }
          }}
        />
        <Button
          variant="contained"
          onClick={() => void handleAdd()}
          disabled={!newLabel.trim() || addMutation.isPending}
          startIcon={addMutation.isPending ? <CircularProgress size={14} /> : undefined}
        >
          Add
        </Button>
        <Tooltip title="Cancel">
          <IconButton aria-label="Cancel adding" onClick={() => setAdding(false)} disabled={addMutation.isPending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Box>
      <FormControl fullWidth size={size} required={required} error={error || isError} disabled={disabled || isLoading}>
        <InputLabel id={`${id}-label`}>{label}</InputLabel>
        <Select
          labelId={`${id}-label`}
          label={label}
          value={list.some((o) => o.id === value) ? value : ''}
          onChange={(e) => {
            if (e.target.value === '__add__') {
              setAdding(true);
              return;
            }
            onChange(String(e.target.value));
          }}
        >
          {list.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
          {mayAdd && (
            <MenuItem value="__add__">
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add {label.toLowerCase()}
            </MenuItem>
          )}
        </Select>
        {(helperText || isError) && (
          <FormHelperText>{isError ? `Could not load the ${label.toLowerCase()} list.` : helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
}
